import { IUserRepository } from "../../adapters/driven/repositories/userRepository";

/**
 * Friendly fallback copy for known reset-password error codes. The server
 * usually ships a populated `message`/`detail`; this helper only kicks in
 * when both are blank. Keeping the strings here (rather than in the UI
 * layer) means new codes only need a one-line entry on the service side
 * and every caller picks them up automatically.
 */
function errorMessageForCode(code: string): string {
  switch (code) {
    case "forbidden":
      return "You don't have permission to reset this user's password.";
    case "clinic_mismatch":
      return "This user belongs to a different clinic. Switch clinics and try again.";
    case "user_not_found":
      return "We couldn't find this user.";
    case "user_not_in_clinic":
      return "This user is no longer in the active clinic.";
    case "cognito_error":
      return "Authentication service couldn't apply the new password. Try again in a moment.";
    case "password_history":
      return "This password was used recently. Pick a different one or tap Generate.";
    case "invalid_password":
      return "That password doesn't meet the account's policy. Pick a longer or more varied one.";
    default:
      return "Could not reset password.";
  }
}

/**
 * Pull a user-facing error from a resend-invite response when the backend
 * indicates failure.
 *
 * Cognito errors travel through the API as
 * `{ detail: { message, error: "An error occurred (XException) when ..." } }`
 * — the AWS exception name is in the parenthesised group. We substring-
 * match instead of regexing both for clarity and because the surrounding
 * boilerplate is stable across AWS SDK versions.
 *
 * Returns `null` when the response shows no failure signal — the caller
 * treats that as the success branch.
 */
function parseSetUserPasswordError(
  r: ISetUserPasswordAPIResponse,
): { message: string; code: string } | null {
  const code = typeof r.code === "string" ? r.code.trim() : "";

  let detailMessage = "";
  let detailError = ""; // Inner Cognito error string when detail is an object.

  if (typeof r.detail === "string") {
    detailMessage = r.detail.trim();
  } else if (r.detail && typeof r.detail === "object") {
    const d = r.detail as { message?: string; error?: string };
    if (typeof d.message === "string") detailMessage = d.message.trim();
    if (typeof d.error === "string") detailError = d.error.trim();
  }

  const topMessage = typeof r.message === "string" ? r.message.trim() : "";

  // No failure signals → caller treats the response as success.
  if (!code && !detailMessage && !detailError && !topMessage) return null;

  // Specific AWS Cognito exceptions get their own copy. Detection is done
  // here (rather than at the UI layer) so every consumer of
  // `userController.setUserPassword` benefits from the same friendly
  // mapping without having to re-parse the AWS string.
  if (detailError.includes("PasswordHistoryPolicyViolationException")) {
    return {
      message: errorMessageForCode("password_history"),
      code: code || "password_history",
    };
  }
  if (detailError.includes("InvalidPasswordException")) {
    return {
      message: errorMessageForCode("invalid_password"),
      code: code || "invalid_password",
    };
  }

  // Otherwise prefer copy the server explicitly authored.
  // `topMessage` first because it's typically the human-friendly summary
  // (e.g. "Caller role not permitted"); `detailMessage` next as a
  // fallback; the raw Cognito string is intentionally NOT shown to end
  // users — it's an SDK artifact.
  const messageText =
    topMessage ||
    detailMessage ||
    errorMessageForCode(code);
  return { message: messageText, code };
}

/**
 * Friendly fallback copy for disconnect-device error codes. Same pattern
 * as `errorMessageForCode` — only used when the server omits its own
 * `message`/`detail`.
 */
function disconnectErrorMessageForCode(code: string): string {
  switch (code) {
    case "forbidden":
      return "You don't have permission to unlink this Circadia Device.";
    case "clinic_mismatch":
      return "This patient belongs to a different clinic. Switch clinics and try again.";
    case "device_not_found":
      return "We couldn't find an active Circadia Device record for this clinic.";
    case "device_patient_mismatch":
      return "The Circadia Device isn't assigned to this patient anymore. Refresh and try again.";
    default:
      return "Could not unlink the Circadia Device.";
  }
}

export const UserServiceImplFactory = ({
  userRepository,
}: {
  userRepository: IUserRepository;
}) => {
  const getUserBetaFeatures: IGetUserBetaFeatures = async () => {
    try {
      const response = await userRepository.getUserBetaFeatures();
      return {
        success: true,
        data: { data: response.data },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateUserInfo: IUpdateUserInfo = async (payload) => {
    try {
      const response = await userRepository.updateUserInfo(payload);
      if ("detail" in response && typeof response.detail === "string") {
        return { success: false, error: { message: response.detail } };
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getAdminPendingUsers: IGetAdminPendingUsers = async (payload) => {
    const email = payload.email?.trim() || undefined;
    const phone = payload.phone?.trim() || undefined;
    if (!email && !phone) {
      return {
        success: false,
        error: {
          message:
            "Provide an email or phone to search pending users (required by the API).",
        },
      };
    }
    const limit =
      payload.limit !== undefined
        ? Math.min(200, Math.max(1, Math.floor(payload.limit)))
        : 50;
    const offset =
      payload.offset !== undefined
        ? Math.max(0, Math.floor(payload.offset))
        : 0;
    try {
      const response = await userRepository.getAdminPendingUsers({
        email,
        phone,
        limit,
        offset,
      });
      if (
        response &&
        typeof response === "object" &&
        "total" in response &&
        typeof (response as IAdminPendingUsersList).total === "number" &&
        "results" in response &&
        Array.isArray((response as IAdminPendingUsersList).results)
      ) {
        return {
          success: true,
          data: response as IAdminPendingUsersList,
        };
      }
      const err = response as { detail?: string; message?: string };
      return {
        success: false,
        error: {
          message:
            (typeof err.detail === "string" && err.detail) ||
            (typeof err.message === "string" && err.message) ||
            "Unable to load pending users.",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const activateAdminPendingUser: IActivateAdminPendingUser = async (
    payload
  ) => {
    try {
      const body: {
        clinic_id: number;
        date_of_admission: string;
        dob?: string;
        gender?: string;
      } = {
        clinic_id: payload.clinic_id,
        date_of_admission: payload.date_of_admission,
      };
      if (payload.dob?.trim()) body.dob = payload.dob.trim();
      if (payload.gender?.trim()) body.gender = payload.gender.trim();

      const response = await userRepository.activateAdminPendingUser({
        cognito_user_id: payload.cognito_user_id,
        body,
      });

      if (response && typeof response === "object") {
        const r = response as Record<string, unknown>;
        if (typeof r.clinic_patient_id === "number" && typeof r.user_id === "string") {
          return {
            success: true,
            data: {
              user_id: r.user_id as string,
              clinic_patient_id: r.clinic_patient_id as number,
              admission_id:
                typeof r.admission_id === "number" ? (r.admission_id as number) : 0,
              message:
                typeof r.message === "string"
                  ? (r.message as string)
                  : "User activated successfully.",
            },
          };
        }
        if (
          typeof r.user_id === "string" &&
          typeof r.message === "string" &&
          r.clinic_patient_id === undefined
        ) {
          return {
            success: true,
            data: {
              user_id: r.user_id as string,
              message: r.message as string,
              alreadyActivated: true,
            },
          };
        }
        const err = response as { detail?: string; message?: string };
        return {
          success: false,
          error: {
            message:
              (typeof err.detail === "string" && err.detail) ||
              (typeof err.message === "string" && err.message) ||
              "Unable to activate user.",
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getClinicUsers: IGetClinicUsers = async (payload) => {
    const limit = Math.min(200, Math.max(1, Math.floor(payload.limit ?? 50)));
    const offset = Math.max(0, Math.floor(payload.offset ?? 0));
    try {
      const response = await userRepository.getClinicUsers({
        clinic_id: payload.clinic_id,
        limit,
        offset,
      });
      if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        Array.isArray((response as IClinicUsersListAPIResponse).data) &&
        "total" in response
      ) {
        return { success: true, data: response as IClinicUsersList };
      }
      const err = response as { detail?: string; message?: string };
      return {
        success: false,
        error: {
          message:
            (typeof err.detail === "string" && err.detail) ||
            (typeof err.message === "string" && err.message) ||
            "Unable to load clinic users.",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  /**
   * "Reset password" → server resend-invite. Overwrites the member's
   * Cognito password to the supplied temporary value and re-sends the
   * invite (email + SMS).
   *
   * PII discipline: the `password` argument is captured by closure here
   * and sent in the request body, but is **never** included in the error
   * path, logs, analytics events, or rejected response shape. Callers
   * must not read it back from `data` — only `message` /
   * `invite_warnings`.
   *
   * Success envelope:
   *   `{ message, invite_warnings }` — `invite_warnings` is a (possibly
   *   empty) array of non-fatal delivery failures (e.g. "SMS failed").
   *   When non-empty the password was still reset; UI should hand the
   *   password off in person rather than trusting the email channel.
   *
   * Error envelope:
   *   The backend returns a `code` discriminator
   *   (`forbidden`/`clinic_mismatch`/`user_not_found`/
   *   `user_not_in_clinic`/`cognito_error`) alongside a human-readable
   *   `message`/`detail`. We surface a friendly fallback per code when
   *   the server didn't include copy of its own.
   */
  const setUserPassword: ISetUserPassword = async (payload) => {
    const clinicId = Number(payload.clinic_id);
    if (!Number.isFinite(clinicId) || clinicId <= 0) {
      return {
        success: false,
        error: { message: "Missing clinic context." },
      };
    }
    const cognitoId = payload.cognito_user_id?.trim();
    if (!cognitoId) {
      return {
        success: false,
        error: { message: "Missing user identifier." },
      };
    }
    if (!payload.password) {
      return {
        success: false,
        error: { message: "Missing new password." },
      };
    }
    try {
      const response = await userRepository.setUserPassword({
        clinic_id: clinicId,
        cognito_user_id: cognitoId,
        body: { password: payload.password },
      });

      if (response && typeof response === "object") {
        const r = response as ISetUserPasswordAPIResponse;

        // Failure detection lives in `parseSetUserPasswordError` so the
        // Cognito-exception mapping (e.g.
        // PasswordHistoryPolicyViolationException → "password used
        // recently") is shared with any future consumer of this service.
        const failure = parseSetUserPasswordError(r);
        if (failure) {
          return {
            success: false,
            error: {
              message: failure.message,
              ...(failure.code ? { code: failure.code } : {}),
            },
          };
        }

        // Success path.
        return {
          success: true,
          data: {
            message:
              typeof r.message === "string" && r.message.trim()
                ? r.message
                : "Invite resent successfully.",
            invite_warnings: Array.isArray(r.invite_warnings)
              ? r.invite_warnings.filter(
                  (w): w is string => typeof w === "string" && Boolean(w),
                )
              : [],
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      // Defensive: the catch path only carries the error message — never
      // the password — so a Sentry / Mixpanel hook upstream cannot
      // accidentally ingest the credential.
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  /**
   * "Unlink Circadia Device" → server disconnect. Detaches the tracker
   * from the patient so it can be reassigned. Validates inputs eagerly so
   * we don't waste a round-trip on missing identifiers, then maps the
   * server's `code` discriminator to a friendly message when the server
   * didn't ship one of its own.
   *
   * Success data carries the updated device row; the UI mainly relies on
   * the message but holding onto the row keeps us forward-compatible if
   * we ever want to display "Available since {timestamp}" or similar.
   */
  const disconnectDevice: IDisconnectDevice = async (payload) => {
    const clinicId = Number(payload.clinic_id);
    if (!Number.isFinite(clinicId) || clinicId <= 0) {
      return {
        success: false,
        error: { message: "Missing clinic context." },
      };
    }
    const patientId = Number(payload.patient_id);
    if (!Number.isFinite(patientId) || patientId <= 0) {
      return {
        success: false,
        error: { message: "Missing patient identifier." },
      };
    }
    const deviceId = payload.device_id?.trim();
    if (!deviceId) {
      return {
        success: false,
        error: { message: "Missing Circadia Device id." },
      };
    }
    try {
      const response = await userRepository.disconnectDevice({
        clinic_id: clinicId,
        patient_id: patientId,
        device_id: deviceId,
      });

      if (response && typeof response === "object") {
        const r = response as IDisconnectDeviceAPIResponse;

        const detailText =
          typeof r.detail === "string" ? r.detail.trim() : "";
        const code = typeof r.code === "string" ? r.code.trim() : "";

        // Error path. Mirrors `setUserPassword` — `code` OR populated
        // `detail` flips us to the rejected branch.
        if (code || detailText) {
          const messageText =
            (typeof r.message === "string" && r.message.trim()) ||
            detailText ||
            disconnectErrorMessageForCode(code);
          return {
            success: false,
            error: {
              message: messageText,
              ...(code ? { code } : {}),
            },
          };
        }

        return {
          success: true,
          data: {
            message:
              typeof r.message === "string" && r.message.trim()
                ? r.message
                : "Circadia Device disconnected successfully.",
            device:
              r.device && typeof r.device === "object"
                ? {
                    id: Number(r.device.id),
                    device_id: String(r.device.device_id ?? ""),
                    clinic_id: Number(r.device.clinic_id),
                    patient_id:
                      r.device.patient_id == null
                        ? null
                        : Number(r.device.patient_id),
                    status: Number(r.device.status),
                  }
                : undefined,
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const signupUser: ISignupUser = async (payload) => {
    try {
      const response = await userRepository.signupUser(payload);

      // Check if the response is a success (has user_id and string message)
      if ("user_id" in response && typeof response.message === "string") {
        return {
          success: true,
          data: {
            message: response.message,
            user_id: response.user_id,
          },
        };
      }
      // Handle validation error response (has string[] message and data: null)
      else if ("message" in response && Array.isArray(response.message)) {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Handle detail error response (has detail string)
      else if ("detail" in response && typeof response.detail === "string") {
        return {
          success: false,
          error: {
            message: undefined,
            detail: response.detail,
          },
        };
      }
      // Handle other message formats
      else if ("message" in response && typeof response.message === "string") {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Fallback for unexpected response format
      else {
        return {
          success: false,
          error: {
            message: "Unexpected response format",
            detail: undefined,
          },
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          message: (error as unknown as Error).message,
          detail: undefined,
        },
      };
    }
  };

  return {
    getUserBetaFeatures,
    updateUserInfo,
    signupUser,
    getAdminPendingUsers,
    activateAdminPendingUser,
    getClinicUsers,
    setUserPassword,
    disconnectDevice,
  };
};
